import { useState, useRef, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Image,
  Badge,
  ListGroup,
  Spinner,
  Dropdown,
  Modal,
} from "react-bootstrap";
import {
  Send,
  Paperclip,
  Smile,
  MoreVertical,
  Search,
  ImageIcon,
  Trash2,
  Edit2,
  X,
} from "lucide-react";
import { FiArrowLeft, FiX, FiMessageCircle } from "react-icons/fi";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import usePageTracking from "../../hooks/usePageTracking";
import {
  activityStart,
  activityEnd,
} from "../../redux/slices/trackActivitySlice";
import { getUser } from "../../utils/auth";
import { getViewProfile } from "../../redux/slices/profileSlice";
import useSocket from "../../hooks/useSocket";
import useChat from "../../hooks/useChat";
import usePlan from "../../hooks/usePlan";
import chatService from "../../api/chatService";
import EmojiPicker from "emoji-picker-react";
import MessagingUpgradeModal from "./MessagingUpgradeModal";
import UserProfileModal from "../community/components/user-profile-modal/UserProfileModal";
import RestrictionModal from "../restriction-modals/RestrictionModal";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import GuidedTour from "../../components/tour/GuidedTour";
import useTour from "../../components/tour/useTour";
import getMessagingTourSteps from "./messagingTourSteps";
import "./Messaging.scss";

const Messaging = ({ onHide }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Track Activity
  usePageTracking("MESSAGING");
  useEffect(() => {
    dispatch(activityStart({ page_name: "MESSAGING", platform: "web" }));
    return () => dispatch(activityEnd({ page_name: "MESSAGING" }));
  }, [dispatch]);
  const { userId } = useParams();
  const location = useLocation();
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageText, setEditingMessageText] = useState("");
  const [showDeleteMessageModal, setShowDeleteMessageModal] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [isDeletingMessage, setIsDeletingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentUser = getUser();

  // Subscription restriction state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // User Profile Modal state
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [
    showProfileRestrictionModal,
    setShowProfileRestrictionModal,
  ] = useState(false);

  const handleUserProfileClick = (e, user) => {
    e.stopPropagation();

    // Free users cannot open the profile modal at all — show the restriction modal
    if (!isProUser) {
      setShowProfileRestrictionModal(true);
      return;
    }

    setSelectedUserProfile(user);
    setShowUserProfileModal(true);
  };

  // Get user from Redux store
  const user = useSelector((state) => state.auth?.user);

  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { isPaid: isProUser } = usePlan();

  // Initialize socket connection
  const { socket, isConnected } = useSocket();

  // Check if user came from notification and show upgrade modal for non-pro users
  useEffect(() => {
    // Check if navigated from notification page (has userId in URL)
    if (userId && !isProUser) {
      console.log(
        "🔒 Non-pro user navigated to messaging from notification, showing upgrade modal",
      );
      setShowSubscriptionModal(true);
    }
  }, [userId, isProUser]);

  // Ensure socket is connected when component mounts
  useEffect(() => {
    if (!isConnected) {
      console.log(
        "⚠️ Socket not connected on Messaging mount, attempting to connect...",
      );
      socket.ensureConnected();
    }
  }, []); // Run once on mount

  // Fetch fresh profile data on mount to ensure plan info is up-to-date
  useEffect(() => {
    dispatch(getViewProfile());
  }, [dispatch]);

  // Get current conversation ID from userId param
  const [currentConversationId, setCurrentConversationId] = useState(null);

  // Use chat hook
  const {
    conversations,
    messages,
    loading,
    error,
    typingUsers,
    onlineUsers,
    sendMessage: sendChatMessage,
    startTyping,
    stopTyping,
    markAllAsRead,
    fetchMessages,
    loadMoreMessages,
    fetchConversations,
    startConversation,
    getOnlineStatus,
    editMessage: editChatMessage,
    deleteMessage: deleteChatMessage,
    hasMore,
    loadingMore,
  } = useChat(currentConversationId, isConnected);

  // State for starting new conversation
  const [startingConversation, setStartingConversation] = useState(false);

  // Find current conversation based on userId param
  // Only show conversation if userId is explicitly provided in URL
  const currentConversation = userId
    ? conversations.find((conv) => {
        const otherUser =
          conv.other_user ||
          (conv.participant_one_id === currentUser?.id
            ? conv.participant_two
            : conv.participant_one);
        return otherUser?.id === parseInt(userId);
      })
    : null; // Don't auto-select any conversation

  // Update conversation ID when conversation changes
  useEffect(() => {
    if (currentConversation) {
      setCurrentConversationId(currentConversation.id);

      // Request online status for the other user
      const otherUser =
        currentConversation.other_user ||
        (currentConversation.participant_one_id === currentUser?.id
          ? currentConversation.participant_two
          : currentConversation.participant_one);

      if (otherUser?.id) {
        console.log("🔍 Requesting online status for user:", otherUser.id);
        getOnlineStatus(otherUser.id);
      }
    }
  }, [currentConversation, currentUser?.id, getOnlineStatus]);

  // Add body class to hide scrollbar when component mounts
  useEffect(() => {
    document.body.classList.add("messaging-active");

    // Cleanup: remove class when component unmounts
    return () => {
      document.body.classList.remove("messaging-active");
    };
  }, []);

  // Don't auto-create conversation - just prepare the UI
  // Conversation will be created when user sends first message
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);

  useEffect(() => {
    // If we have a userId but no conversation, store it as pending and fetch user data
    const fetchPendingUser = async () => {
      if (userId && !currentConversation && !loading) {
        const targetUserId = parseInt(userId);
        setPendingUserId(targetUserId);

        // First, check if user data was passed via navigation state
        if (location.state?.pendingUser) {
          setPendingUser(location.state.pendingUser);
          return;
        }

        // Otherwise, try to find user data from existing conversations
        const existingConv = conversations.find((conv) => {
          const otherUser =
            conv.other_user ||
            (conv.participant_one_id === currentUser?.id
              ? conv.participant_two
              : conv.participant_one);
          return otherUser?.id === targetUserId;
        });

        if (existingConv) {
          const otherUser =
            existingConv.other_user ||
            (existingConv.participant_one_id === currentUser?.id
              ? existingConv.participant_two
              : existingConv.participant_one);
          setPendingUser(otherUser);
        }
      } else if (currentConversation) {
        setPendingUserId(null);
        setPendingUser(null);
      }
    };

    fetchPendingUser();
  }, [
    userId,
    currentConversation,
    loading,
    conversations,
    currentUser?.id,
    location.state,
  ]);

  // Request online status for all users in conversations
  useEffect(() => {
    if (conversations.length > 0 && isConnected) {
      // Request status for all users
      conversations.forEach((conv) => {
        const otherUser =
          conv.other_user ||
          (conv.participant_one_id === currentUser?.id
            ? conv.participant_two
            : conv.participant_one);

        if (otherUser?.id) {
          getOnlineStatus(otherUser.id);
        }
      });

      // Set up periodic status check every 10 seconds
      const intervalId = setInterval(() => {
        console.log(
          "🔄 Periodic status check - requesting status for all users",
        );
        conversations.forEach((conv) => {
          const otherUser =
            conv.other_user ||
            (conv.participant_one_id === currentUser?.id
              ? conv.participant_two
              : conv.participant_one);

          if (otherUser?.id) {
            getOnlineStatus(otherUser.id);
          }
        });
      }, 10000); // 10 seconds

      return () => clearInterval(intervalId);
    }
  }, [conversations, isConnected, currentUser?.id, getOnlineStatus]);

  // Refresh online status when page becomes visible (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && conversations.length > 0 && isConnected) {
        console.log("👁️ Page visible - refreshing online status for all users");
        conversations.forEach((conv) => {
          const otherUser =
            conv.other_user ||
            (conv.participant_one_id === currentUser?.id
              ? conv.participant_two
              : conv.participant_one);

          if (otherUser?.id) {
            getOnlineStatus(otherUser.id);
          }
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [conversations, isConnected, currentUser?.id, getOnlineStatus]);

  // Refresh online status when socket reconnects
  useEffect(() => {
    if (isConnected && conversations.length > 0) {
      console.log(
        "🔌 Socket connected - refreshing online status for all users",
      );
      conversations.forEach((conv) => {
        const otherUser =
          conv.other_user ||
          (conv.participant_one_id === currentUser?.id
            ? conv.participant_two
            : conv.participant_one);

        if (otherUser?.id) {
          getOnlineStatus(otherUser.id);
        }
      });
    }
  }, [isConnected]); // Only trigger when isConnected changes

  // Get conversation data
  const conversation = currentConversation
    ? {
        id: currentConversation.id,
        user:
          currentConversation.other_user ||
          (currentConversation.participant_one_id === currentUser?.id
            ? currentConversation.participant_two
            : currentConversation.participant_one),
      }
    : pendingUser
    ? {
        id: null, // No conversation ID yet
        user: pendingUser,
      }
    : null;

  // Check if other user is online
  const isOtherUserOnline = conversation?.user?.id
    ? onlineUsers.get(conversation.user.id) === true
    : false;

  // Check if other user is typing
  const isOtherUserTyping = conversation?.user?.id
    ? typingUsers.has(conversation.user.id)
    : false;

  // Auto-scroll to bottom when new messages arrive (not on initial load)
  const prevMessagesLengthRef = useRef(0);
  const prevScrollHeightRef = useRef(0);

  useEffect(() => {
    if (messages.length > 0) {
      // Only scroll if messages were added (not on initial load)
      if (
        prevMessagesLengthRef.current > 0 &&
        messages.length > prevMessagesLengthRef.current
      ) {
        scrollToBottom();
      } else if (prevMessagesLengthRef.current === 0) {
        // On initial load, scroll without animation to avoid page jump
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({
            behavior: "auto",
            block: "end",
          });
        }
      }
      prevMessagesLengthRef.current = messages.length;
    }
  }, [messages.length]);

  // Handle scroll to load more messages
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current || !hasMore || loadingMore) return;

    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;

    // If scrolled to top (within 100px), load more messages
    if (scrollTop < 100) {
      console.log("📜 Loading more messages...");

      // Save current scroll height before loading
      prevScrollHeightRef.current = container.scrollHeight;

      loadMoreMessages(currentConversationId);
    }
  }, [hasMore, loadingMore, currentConversationId, loadMoreMessages]);

  // Attach scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Restore scroll position after loading more messages
  useEffect(() => {
    if (loadingMore || !messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const currentScrollHeight = container.scrollHeight;
    const previousScrollHeight = prevScrollHeightRef.current;

    // If scroll height changed (new messages loaded), maintain scroll position
    if (
      previousScrollHeight > 0 &&
      currentScrollHeight > previousScrollHeight
    ) {
      const scrollDiff = currentScrollHeight - previousScrollHeight;
      container.scrollTop = scrollDiff;
      console.log("📍 Restored scroll position after loading older messages");
    }
  }, [messages, loadingMore]);

  // Mark messages as read when opening conversation
  useEffect(() => {
    if (currentConversationId && messages.length > 0) {
      // Only mark as read if there are unread messages that the current user RECEIVED
      const hasUnreadReceivedMessages = messages.some(
        (msg) => msg.receiver_id === currentUser?.id && !msg.is_read,
      );

      if (hasUnreadReceivedMessages) {
        // Mark all as read after a short delay
        const timer = setTimeout(() => {
          markAllAsRead(currentConversationId);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [
    currentConversationId,
    messages.length,
    markAllAsRead,
    currentUser?.id,
    messages,
  ]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Guided tour — gated to the two conditions requested:
  //   1. only SUBSCRIBED (Pro) users — `isProUser`
  //   2. only when at least one conversation exists — `conversations.length > 0`
  // A free user, or a subscribed user with no conversations, gets no tour.
  // GuidedTour is rendered only in the "conversation open" view below (where
  // both the list and chat elements are mounted), so it surfaces once the user
  // opens a conversation. Declared before the early returns to keep hook order
  // stable. Replayable from the header tour button (pageKey "messaging").
  const canRunMessagingTour = isProUser && conversations.length > 0;
  const { isOpen: isTourOpen, finish: finishTour } = useTour("messaging", {
    autoStart: canRunMessagingTour,
  });
  // On mobile the "conversation open" view hides the list, so the list-oriented
  // tour steps are dropped (see messagingTourSteps). Track viewport reactively
  // so the step set is correct if the user rotates/resizes mid-session.
  const [isDesktopTour, setIsDesktopTour] = useState(
    typeof window !== "undefined" ? window.innerWidth > 600 : true,
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTour(window.innerWidth > 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Handle emoji selection
  const handleEmojiClick = (emojiData) => {
    setMessage((prevMessage) => prevMessage + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  // Toggle emoji picker
  const toggleEmojiPicker = () => {
    // Check subscription restriction for non-pro users
    if (!isProUser) {
      setShowSubscriptionModal(true);
      return;
    }

    setShowEmojiPicker((prev) => !prev);
  };

  const scrollToBottom = () => {
    // Only scroll the messages container, not the whole page
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!message.trim() && !selectedImage) || isSending) return;

    // If we have a pending user (no conversation yet), create it first
    if (pendingUserId && !conversation) {
      setIsSending(true);
      try {
        const newConversation = await startConversation(pendingUserId);
        if (newConversation) {
          setCurrentConversationId(newConversation.id);
          setPendingUserId(null);
          // Refresh conversations list
          await fetchConversations();
          // Wait a bit for state to update
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (err) {
        console.error("Error creating conversation:", err);
        alert("Failed to start conversation. Please try again.");
        setIsSending(false);
        return;
      }
    }

    if (!conversation) {
      setIsSending(false);
      return;
    }

    setIsSending(true);

    try {
      let attachmentUrl = null;
      let messageType = "text";

      // Upload image if selected
      if (selectedImage) {
        setIsUploading(true);
        try {
          const response = await chatService.uploadImage(selectedImage);
          if (response.data.success) {
            attachmentUrl = response.data.data.file_url;
            messageType = "image";
          }
        } catch (uploadError) {
          console.error("Error uploading image:", uploadError);
          alert("Failed to upload image. Please try again.");
          setIsUploading(false);
          setIsSending(false);
          return;
        } finally {
          setIsUploading(false);
        }
      }

      // Send message via socket
      await sendChatMessage({
        receiver_id: conversation.user.id,
        message: message.trim() || "📷 Photo",
        conversation_id: currentConversationId,
        message_type: messageType,
        attachment_url: attachmentUrl,
      });

      setMessage("");
      setSelectedImage(null);
      setImagePreview(null);

      // Stop typing indicator
      if (currentConversationId) {
        stopTyping(currentConversationId);
      }

      // Scroll to bottom after sending
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSending(false);
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    // Check subscription restriction for non-pro users
    if (!isProUser) {
      e.preventDefault();
      setShowSubscriptionModal(true);
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    setSelectedImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Trigger file input click
  const handleImageButtonClick = () => {
    // Check subscription restriction for non-pro users
    if (!isProUser) {
      setShowSubscriptionModal(true);
      return;
    }

    fileInputRef.current?.click();
  };

  // Handle typing
  const handleTyping = (e) => {
    // Check subscription restriction for non-pro users
    if (!isProUser) {
      e.preventDefault();
      setShowSubscriptionModal(true);
      return;
    }

    setMessage(e.target.value);

    // Start typing indicator
    if (currentConversationId && e.target.value.trim()) {
      startTyping(currentConversationId);
    } else if (currentConversationId) {
      stopTyping(currentConversationId);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString();
    }
  };

  const goBack = () => {
    navigate("/community");
  };

  const formatConversationTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffMinutes < 1) {
      return "Just now";
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Handle delete conversation
  const handleDeleteConversation = async () => {
    if (!currentConversationId) return;

    setIsDeleting(true);
    try {
      await chatService.deleteConversation(currentConversationId);

      // Close modal
      setShowDeleteModal(false);

      // Refresh conversations list
      await fetchConversations();

      // Navigate to messaging page without userId
      navigate("/messaging");
    } catch (error) {
      console.error("Error deleting conversation:", error);
      alert("Failed to delete conversation. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle edit message
  const handleEditMessage = (messageId, currentText) => {
    setEditingMessageId(messageId);
    setEditingMessageText(currentText);
  };

  // Handle save edited message
  const handleSaveEditedMessage = () => {
    if (!editingMessageText.trim() || !editingMessageId) return;

    editChatMessage(
      editingMessageId,
      editingMessageText.trim(),
      currentConversationId,
    );

    // Clear editing state
    setEditingMessageId(null);
    setEditingMessageText("");
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingMessageText("");
  };

  // Handle delete message
  const handleDeleteMessage = (messageId) => {
    setDeletingMessageId(messageId);
    setShowDeleteMessageModal(true);
  };

  // Confirm delete message
  const confirmDeleteMessage = () => {
    if (!deletingMessageId) return;

    setIsDeletingMessage(true);
    try {
      deleteChatMessage(deletingMessageId, currentConversationId);

      // Close modal
      setShowDeleteMessageModal(false);
      setDeletingMessageId(null);
    } catch (error) {
      console.error("Error deleting message:", error);
      alert("Failed to delete message. Please try again.");
    } finally {
      setIsDeletingMessage(false);
    }
  };

  // Check if message can be edited or deleted
  const canEditOrDeleteMessage = (msg) => {
    // Must be own message
    if (msg.sender_id !== currentUser?.id) return false;

    // Cannot edit/delete if already deleted
    if (msg.is_deleted) return false;

    // Cannot edit/delete if optimistic (not yet confirmed)
    if (msg.isOptimistic) return false;

    // Cannot edit/delete if receiver has read the message
    if (msg.is_read) return false;

    // Cannot edit/delete if more than 2 hours have passed
    const messageTime = new Date(msg.created_at).getTime();
    const currentTime = new Date().getTime();
    const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    const timeDifference = currentTime - messageTime;

    if (timeDifference > twoHoursInMs) return false;

    return true;
  };

  // Get user avatar URL
  const getUserAvatar = (user) => {
    // If user has profile photo path
    if (user?.profile_photo_path) {
      const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:1111";

      // Check if it's already a full URL
      if (
        user.profile_photo_path.startsWith("http://") ||
        user.profile_photo_path.startsWith("https://")
      ) {
        return user.profile_photo_path;
      }

      // Construct full URL
      const photoPath = user.profile_photo_path.startsWith("uploads/")
        ? user.profile_photo_path
        : `uploads/user_profile/${user.profile_photo_path}`;

      return `${API_BASE_URL}/${photoPath}`;
    }

    // Fallback to UI Avatars
    const userName =
      `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
      user?.email ||
      "User";
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      userName,
    )}&background=0195D3&color=fff&size=40`;
  };

  // Get user display name
  const getUserName = (user) => {
    if (user?.first_name || user?.last_name) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim();
    }
    return user?.email || "Unknown User";
  };

  const filteredConversations = conversations.filter((conv) => {
    const otherUser =
      conv.other_user ||
      (conv.participant_one_id === currentUser?.id
        ? conv.participant_two
        : conv.participant_one);
    const userName = getUserName(otherUser);
    return userName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Show loading state
  if ((loading && conversations.length === 0) || startingConversation) {
    return (
      <div className={`messaging-page ${userId ? "has-active-chat" : ""}`}>
        {/* <div className="app-page-title">
          <div className="page-title-heading d-flex align-items-center">
            <FiArrowLeft
              size={40}
              className="icon cursor-pointer"
              onClick={goBack}
            />
            <h3 className="page-title">Messages</h3>
          </div>
        </div> */}
        <Container
          fluid
          className="messaging-container d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">
              {startingConversation ? "Starting conversation..." : "Loading..."}
            </p>
          </div>
        </Container>
      </div>
    );
  }

  // Show error state
  if (error && conversations.length === 0) {
    return (
      <div className={`messaging-page ${userId ? "has-active-chat" : ""}`}>
        <div className="app-page-title">
          <div className="page-title-heading d-flex align-items-center">
            <FiArrowLeft
              size={40}
              className="icon cursor-pointer"
              onClick={goBack}
            />
            <h3 className="page-title">Messages</h3>
          </div>
        </div>
        <Container fluid className="messaging-container">
          <div className="alert alert-danger m-3">{error}</div>
        </Container>
      </div>
    );
  }

  // Show empty state when no conversations exist
  if (!loading && conversations.length === 0 && !userId) {
    return (
      <div className="messaging-page">
        {/* <div className="app-page-title">
          <div className="page-title-heading d-flex align-items-center">
            <FiArrowLeft
              size={40}
              className="icon cursor-pointer"
              onClick={goBack}
            />
            <h3 className="page-title">Messages</h3>
          </div>
        </div> */}
        <Container fluid className="messaging-container">
          <div className="text-center p-5">
            <h5>No conversations yet</h5>
            <p className="text-muted">
              Start a conversation from the community page by clicking the
              post's user profile icon.
            </p>
          </div>
        </Container>
      </div>
    );
  }

  // Show conversation list with empty chat area when no conversation is selected
  if (!conversation) {
    return (
      <div className={`messaging-page ${userId ? "has-active-chat" : ""}`}>
        <Container fluid className="messaging-container">
          <Row className="h-100">
            {/* Left Sidebar - Conversations List */}
            <Col xs={12} md={4} className="conversations-sidebar p-0">
              <Card className="conversations-card h-100">
                <Card.Header className="conversations-header">
                  <h5>Messages</h5>
                  <div className="search-container" data-tour="ms-search">
                    <Search size={16} className="search-icon" />
                    <Form.Control
                      type="text"
                      placeholder="Search messages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </Card.Header>
                <Card.Body className="conversations-list p-0" data-tour="ms-list">
                  <ListGroup variant="flush">
                    {filteredConversations.map((conv) => {
                      const otherUser =
                        conv.other_user ||
                        (conv.participant_one_id === currentUser?.id
                          ? conv.participant_two
                          : conv.participant_one);
                      const isOnline = onlineUsers.get(otherUser?.id) === true;
                      const lastMsg = conv.last_message;

                      return (
                        <ListGroup.Item
                          key={conv.id}
                          className="conversation-item"
                          onClick={() =>
                            navigate(`/messaging/${otherUser?.id}`)
                          }
                        >
                          <div className="d-flex align-items-start">
                            <div className="position-relative flex-shrink-0">
                              <Image
                                src={getUserAvatar(otherUser)}
                                alt={getUserName(otherUser)}
                                roundedCircle
                                width={42}
                                height={42}
                                style={{
                                  cursor: "pointer",
                                  objectFit: "cover",
                                }}
                                onClick={(e) =>
                                  handleUserProfileClick(e, otherUser)
                                }
                                onError={(e) => {
                                  e.target.onerror = null;
                                  const userName = getUserName(otherUser);
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                    userName,
                                  )}&background=0195D3&color=fff&size=42`;
                                }}
                              />
                              {isOnline && (
                                <div className="online-indicator"></div>
                              )}
                            </div>

                            <div className="flex-grow-1 min-w-0">
                              <div className="d-flex justify-content-between align-items-start">
                                <h6 className="conversation-name mb-0">
                                  {getUserName(otherUser)}
                                </h6>
                                <small className="conversation-time">
                                  {conv.last_message_at
                                    ? formatConversationTime(
                                        conv.last_message_at,
                                      )
                                    : ""}
                                </small>
                              </div>
                              <div className="d-flex justify-content-between align-items-center">
                                <p className="conversation-preview mb-0">
                                  {lastMsg?.message || "No messages yet"}
                                </p>
                                {conv.unread_count > 0 && (
                                  <Badge
                                    bg="primary"
                                    className="unread-badge flex-shrink-0"
                                  >
                                    {conv.unread_count}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            {/* Center - Chat Area - Empty State */}
            <Col xs={12} md={8} className="chat-area p-0">
              <Card className="messaging-card h-100">
                <Card.Body className="d-flex align-items-center justify-content-center">
                  <div className="text-center p-5">
                    <FiMessageCircle size={64} className="text-muted mb-3" />
                    <h5>Select a conversation</h5>
                    <p className="text-muted">
                      Choose a conversation from the list to start messaging
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        <UserProfileModal
          show={showUserProfileModal}
          onHide={() => setShowUserProfileModal(false)}
          userId={selectedUserProfile?.id}
          userName={selectedUserProfile ? getUserName(selectedUserProfile) : ""}
          userImage={selectedUserProfile?.profile_photo_path}
          userState={selectedUserProfile?.state || ""}
        />

        <RestrictionModal
          isVisible={showProfileRestrictionModal}
          onClose={() => setShowProfileRestrictionModal(false)}
          eventSource="follow_limit"
          iconType="user-follow"
          heading="Want to follow or chat with this user?"
          subText="Upgrade to connect and build real relationships."
          infoText="Pro users can follow, chat, and connect with anyone in the community."
        />
      </div>
    );
  }

  return (
    <>
      <NoIndexSEO title={seoConfig.messaging.title} />
      <GuidedTour
        isOpen={isTourOpen && canRunMessagingTour}
        steps={getMessagingTourSteps(isDesktopTour)}
        onDone={finishTour}
      />
      <div className={`messaging-page ${userId ? "has-active-chat" : ""}`}>
        {/* Header like SearchDetail */}
        {/* <div className="app-page-title">
        <div className="page-title-heading d-flex align-items-center">
          <FiArrowLeft
            size={40}
            className="icon cursor-pointer"
            onClick={goBack}
          />
          <h3 className="page-title">Messages</h3>
        </div>
      </div> */}

        <Container fluid className="messaging-container position-relative">
          <Row className="h-100">
            {/* Left Sidebar - Conversations List */}
            <Col xs={12} md={4} className="conversations-sidebar p-0">
              <Card className="conversations-card h-100">
                <Card.Header className="conversations-header">
                  <h5>Messages</h5>
                  <div className="search-container" data-tour="ms-search">
                    <Search size={16} className="search-icon" />
                    <Form.Control
                      type="text"
                      placeholder="Search messages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </Card.Header>

                <Card.Body className="conversations-list p-0" data-tour="ms-list">
                  <ListGroup variant="flush">
                    {filteredConversations.map((conv, idx) => {
                      const otherUser =
                        conv.other_user ||
                        (conv.participant_one_id === currentUser?.id
                          ? conv.participant_two
                          : conv.participant_one);
                      const isOnline = onlineUsers.get(otherUser?.id) === true;
                      const lastMsg = conv.last_message;

                      return (
                        <ListGroup.Item
                          key={conv.id}
                          data-tour={idx === 0 ? "ms-conv1" : undefined}
                          className={`conversation-item ${
                            conv.id === currentConversationId ? "active" : ""
                          }`}
                          onClick={() =>
                            navigate(`/messaging/${otherUser?.id}`)
                          }
                        >
                          <div className="d-flex align-items-start">
                            <div className="position-relative flex-shrink-0">
                              <Image
                                src={getUserAvatar(otherUser)}
                                alt={getUserName(otherUser)}
                                roundedCircle
                                width={42}
                                height={42}
                                style={{
                                  cursor: "pointer",
                                  objectFit: "cover",
                                }}
                                onClick={(e) =>
                                  handleUserProfileClick(e, otherUser)
                                }
                                onError={(e) => {
                                  e.target.onerror = null;
                                  const userName = getUserName(otherUser);
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                    userName,
                                  )}&background=0195D3&color=fff&size=42`;
                                }}
                              />
                              {isOnline && (
                                <div className="online-indicator"></div>
                              )}
                            </div>

                            <div className="flex-grow-1 min-w-0">
                              <div className="d-flex justify-content-between align-items-start">
                                <h6 className="conversation-name mb-0">
                                  {getUserName(otherUser)}
                                </h6>
                                <small className="conversation-time">
                                  {conv.last_message_at
                                    ? formatConversationTime(
                                        conv.last_message_at,
                                      )
                                    : ""}
                                </small>
                              </div>
                              <div className="d-flex justify-content-between align-items-center">
                                <p className="conversation-preview mb-0">
                                  {lastMsg?.message || "No messages yet"}
                                </p>
                                {conv.unread_count > 0 &&
                                  conv.id !== currentConversationId && (
                                    <Badge
                                      bg="primary"
                                      className="unread-badge flex-shrink-0"
                                    >
                                      {conv.unread_count}
                                    </Badge>
                                  )}
                              </div>
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            {/* Center - Chat Area (Now takes full remaining width) */}
            <Col xs={12} md={8} className="chat-area p-0">
              <Card className="messaging-card h-100">
                {/* Chat Header */}
                <Card.Header className="chat-header" data-tour="ms-chatheader">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1">
                      {/* Mobile Back Button - Only visible on mobile */}
                      <Button
                        variant="link"
                        className="mobile-back-btn p-0 me-2"
                        onClick={() => navigate("/messaging")}
                        aria-label="Back to conversations"
                      >
                        <FiArrowLeft size={24} />
                      </Button>

                      <div className="position-relative flex-shrink-0">
                        <Image
                          src={getUserAvatar(conversation.user)}
                          alt={getUserName(conversation.user)}
                          roundedCircle
                          width={40}
                          height={40}
                          className="chat-avatar"
                          style={{ cursor: "pointer", objectFit: "cover" }}
                          onClick={(e) =>
                            handleUserProfileClick(e, conversation.user)
                          }
                          onError={(e) => {
                            e.target.onerror = null;
                            const userName = getUserName(conversation.user);
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              userName,
                            )}&background=0195D3&color=fff&size=40`;
                          }}
                        />
                        {isOtherUserOnline && (
                          <div className="online-indicator-header"></div>
                        )}
                      </div>

                      <div className="flex-grow-1">
                        <div
                          className="user-name"
                          style={{ cursor: "pointer" }}
                          onClick={(e) =>
                            handleUserProfileClick(e, conversation.user)
                          }
                        >
                          {getUserName(conversation.user)}
                        </div>
                        <div className="user-status">
                          {isOtherUserTyping ? (
                            <small className="text-muted typing-text">
                              typing...
                            </small>
                          ) : (
                            <span className="status-text">
                              {isOtherUserOnline ? "Active now" : "Offline"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as={Button}
                        variant="link"
                        className="more-btn p-0"
                        id="conversation-options-dropdown"
                        data-tour="ms-menu"
                      >
                        <MoreVertical size={20} />
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => setShowDeleteModal(true)}
                          className="text-danger"
                        >
                          <Trash2 size={16} />
                          Delete Conversation
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </Card.Header>

                {/* Messages */}
                <Card.Body className="messages-container" data-tour="ms-messages">
                  <div className="messages-list" ref={messagesContainerRef}>
                    {loadingMore && (
                      <div className="text-center py-2">
                        <Spinner
                          animation="border"
                          size="sm"
                          variant="primary"
                        />
                        <small className="ms-2 text-muted">
                          Loading older messages...
                        </small>
                      </div>
                    )}

                    {messages.length === 0 ? (
                      // Empty state - no messages yet
                      <div className="no-messages-state text-center py-5">
                        <FiMessageCircle
                          size={48}
                          className="text-muted mb-3"
                        />
                        <h5 className="text-muted mb-2">No messages yet</h5>
                        <p className="text-muted small mb-0">
                          Start the conversation by sending a message to{" "}
                          {getUserName(conversation.user)}
                        </p>
                      </div>
                    ) : (
                      // Messages list
                      messages
                        .filter((msg) => msg && msg.id) // Filter out invalid messages
                        .map((msg, index) => {
                          const isOwnMessage =
                            msg.sender_id === currentUser?.id;
                          const showDate =
                            index === 0 ||
                            !messages[index - 1] ||
                            formatDate(messages[index - 1]?.created_at) !==
                              formatDate(msg.created_at);

                          return (
                            <div key={msg.id}>
                              {showDate && (
                                <div className="date-divider">
                                  <span>{formatDate(msg.created_at)}</span>
                                </div>
                              )}

                              <div
                                className={`message-wrapper ${
                                  isOwnMessage ? "own-message" : "other-message"
                                }`}
                              >
                                {!isOwnMessage && (
                                  <Image
                                    src={getUserAvatar(
                                      msg.sender || conversation.user,
                                    )}
                                    alt={getUserName(
                                      msg.sender || conversation.user,
                                    )}
                                    roundedCircle
                                    width={28}
                                    height={28}
                                    className="message-avatar"
                                    style={{
                                      cursor: "pointer",
                                      objectFit: "cover",
                                    }}
                                    onClick={(e) =>
                                      handleUserProfileClick(
                                        e,
                                        msg.sender || conversation.user,
                                      )
                                    }
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      const userName = getUserName(
                                        msg.sender || conversation.user,
                                      );
                                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                        userName,
                                      )}&background=0195D3&color=fff&size=28`;
                                    }}
                                  />
                                )}

                                <div className="message-content-wrapper">
                                  {/* Edit mode */}
                                  {editingMessageId === msg.id ? (
                                    <div className="message-edit-container">
                                      <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={editingMessageText}
                                        onChange={(e) =>
                                          setEditingMessageText(e.target.value)
                                        }
                                        className="message-edit-input"
                                        autoFocus
                                      />
                                      <div className="message-edit-actions">
                                        <Button
                                          size="sm"
                                          variant="link"
                                          onClick={handleCancelEdit}
                                          className="text-muted"
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="primary"
                                          onClick={handleSaveEditedMessage}
                                          disabled={!editingMessageText.trim()}
                                        >
                                          Save
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="message-bubble-container">
                                        <div className="message-bubble">
                                          {msg.message_type === "image" &&
                                          msg.attachment_url ? (
                                            <div className="message-image-container">
                                              <img
                                                src={`${process.env.REACT_APP_API_BASE_URL}/uploads/chat_images/${msg.attachment_url}`}
                                                alt="Shared image"
                                                className="message-image"
                                                onClick={() =>
                                                  window.open(
                                                    `${process.env.REACT_APP_API_BASE_URL}/uploads/chat_images/${msg.attachment_url}`,
                                                    "_blank",
                                                  )
                                                }
                                              />
                                              {msg.message &&
                                                msg.message.trim() &&
                                                msg.message !== "📷 Photo" && (
                                                  <div className="message-caption">
                                                    {msg.message}
                                                  </div>
                                                )}
                                            </div>
                                          ) : (
                                            <div className="message-content">
                                              {msg.message}
                                              {msg.is_edited &&
                                                !msg.is_deleted && (
                                                  <span className="edited-indicator">
                                                    {" "}
                                                    (edited)
                                                  </span>
                                                )}
                                            </div>
                                          )}
                                        </div>

                                        {/* Message options dropdown - only for own messages that can be edited/deleted */}
                                        {canEditOrDeleteMessage(msg) && (
                                          <Dropdown
                                            className="message-options-dropdown"
                                            align="end"
                                          >
                                            <Dropdown.Toggle
                                              as={Button}
                                              variant="link"
                                              className="message-options-btn p-0"
                                              size="sm"
                                            >
                                              <MoreVertical size={14} />
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu>
                                              {msg.message_type !== "image" && (
                                                <Dropdown.Item
                                                  onClick={() =>
                                                    handleEditMessage(
                                                      msg.id,
                                                      msg.message,
                                                    )
                                                  }
                                                >
                                                  <Edit2
                                                    size={14}
                                                    className="me-2"
                                                  />
                                                  Edit
                                                </Dropdown.Item>
                                              )}
                                              <Dropdown.Item
                                                onClick={() =>
                                                  handleDeleteMessage(msg.id)
                                                }
                                                className="text-danger"
                                              >
                                                <Trash2
                                                  size={14}
                                                  className="me-2"
                                                />
                                                Delete
                                              </Dropdown.Item>
                                            </Dropdown.Menu>
                                          </Dropdown>
                                        )}
                                      </div>

                                      <div className="message-meta">
                                        <span className="message-time">
                                          {formatTime(msg.created_at)}
                                        </span>
                                        {isOwnMessage && msg.is_read && (
                                          <span
                                            className="read-receipt"
                                            title="Read"
                                          >
                                            ✓✓
                                          </span>
                                        )}
                                        {isOwnMessage &&
                                          !msg.is_read &&
                                          !msg.isOptimistic && (
                                            <span
                                              className="read-receipt"
                                              title="Sent"
                                            >
                                              ✓
                                            </span>
                                          )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </Card.Body>

                {/* Message Input */}
                <Card.Footer className="message-input-container" data-tour="ms-inputbar">
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="image-preview-container">
                      <div className="image-preview-wrapper">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="image-preview"
                        />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={handleRemoveImage}
                          disabled={isUploading || isSending}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}

                  <Form
                    onSubmit={handleSendMessage}
                    className="d-flex align-items-center"
                  >
                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: "none" }}
                    />

                    {/* Image upload button */}
                    <Button
                      variant="link"
                      className="attachment-btn p-0 me-2"
                      onClick={handleImageButtonClick}
                      disabled={!isConnected || isSending || isUploading}
                      type="button"
                      data-tour="ms-attach"
                    >
                      <ImageIcon size={20} />
                    </Button>

                    <Form.Control
                      type="text"
                      placeholder={
                        isProUser
                          ? "Type a message..."
                          : "Upgrade to Pro to send messages"
                      }
                      value={message}
                      onChange={handleTyping}
                      onFocus={(e) => {
                        if (!isProUser) {
                          e.target.blur();
                          setShowSubscriptionModal(true);
                        }
                      }}
                      className="message-input flex-grow-1"
                      data-tour="ms-input"
                      disabled={!isConnected || isSending || isUploading}
                      readOnly={!isProUser}
                      style={{
                        cursor: !isProUser ? "pointer" : "text",
                      }}
                    />

                    <div className="emoji-picker-wrapper" ref={emojiPickerRef}>
                      <Button
                        variant="link"
                        className="emoji-btn p-0 mx-2"
                        onClick={toggleEmojiPicker}
                        disabled={!isConnected || isSending || isUploading}
                        type="button"
                      >
                        <Smile size={20} />
                      </Button>

                      {showEmojiPicker && (
                        <div className="emoji-picker-container">
                          <EmojiPicker
                            onEmojiClick={handleEmojiClick}
                            width={320}
                            height={400}
                            searchDisabled={false}
                            skinTonesDisabled={false}
                            previewConfig={{ showPreview: false }}
                          />
                        </div>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="send-btn"
                      data-tour="ms-send"
                      disabled={
                        (!message.trim() && !selectedImage) ||
                        !isConnected ||
                        isSending ||
                        isUploading ||
                        !isProUser
                      }
                    >
                      {isSending || isUploading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Send size={18} />
                      )}
                    </Button>
                  </Form>
                  {!isConnected && (
                    <div className="text-center mt-2">
                      <small className="text-danger">
                        Connecting to chat server...
                      </small>
                    </div>
                  )}
                  {isUploading && (
                    <div className="text-center mt-2">
                      <small className="text-primary">Uploading image...</small>
                    </div>
                  )}
                </Card.Footer>
              </Card>
            </Col>
          </Row>

          {/* Messaging upgrade — custom-overlay modal preserved for this page */}
          <MessagingUpgradeModal
            show={showSubscriptionModal}
            eventSource="messaging_notification_click"
          />
        </Container>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
          size="md"
          className="messaging-delete-modal"
          dialogClassName="custom-modal profile-other-details-modal"
        >
          <Modal.Header>
            <h5 className="modal-title">Delete Conversation</h5>
            <Button
              className="close-btn"
              onClick={() => setShowDeleteModal(false)}
            >
              <FiX size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body>
            <p className="text-center">
              Are you sure you want to delete this conversation?
            </p>

            <div className="text-center mt-4">
              <Button
                // size="sm"
                variant="outline-danger"
                onClick={handleDeleteConversation}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Delete Message Confirmation Modal */}
        <Modal
          show={showDeleteMessageModal}
          onHide={() => setShowDeleteMessageModal(false)}
          centered
          size="md"
          className="messaging-delete-modal"
          dialogClassName="custom-modal profile-other-details-modal"
        >
          <Modal.Header>
            <h5 className="modal-title">Delete Message</h5>
            <Button
              className="close-btn"
              onClick={() => setShowDeleteMessageModal(false)}
            >
              <FiX size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body>
            <p className="text-center">
              Are you sure you want to delete this message?
            </p>

            <div className="text-center mt-4">
              <Button
                variant="outline-danger"
                onClick={confirmDeleteMessage}
                disabled={isDeletingMessage}
              >
                {isDeletingMessage ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        <UserProfileModal
          show={showUserProfileModal}
          onHide={() => setShowUserProfileModal(false)}
          userId={selectedUserProfile?.id}
          userName={selectedUserProfile ? getUserName(selectedUserProfile) : ""}
          userImage={selectedUserProfile?.profile_photo_path}
          userState={selectedUserProfile?.state || ""}
        />

        <RestrictionModal
          isVisible={showProfileRestrictionModal}
          onClose={() => setShowProfileRestrictionModal(false)}
          eventSource="follow_limit"
          iconType="user-follow"
          heading="Want to follow or chat with this user?"
          subText="Upgrade to connect and build real relationships."
          infoText="Pro users can follow, chat, and connect with anyone in the community."
        />
      </div>
    </>
  );
};

export default Messaging;
